/**
 * Node inspector. Progressive disclosure from the route down to the exact
 * commit, agent, session, build and acceptance criteria — and, at a station,
 * the approval action itself.
 *
 * Approval is a deliberate human act with a reason. It is never inferred from
 * a green build.
 */
import { useState } from 'react';
import type { JourneyNodeView, JourneyView } from '@dcc/shared';
import { Confidence, FreshnessTag, StateChip, Unknown } from './Primitives';
import { absoluteTime, relativeTime } from '../lib/format';
import { api, idempotencyKey, ApiRequestError } from '../lib/api';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="inspector__field">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function JourneyInspector({
  node,
  journey,
  projectId,
  canApprove,
  onChanged,
}: {
  node: JourneyNodeView | null;
  journey: JourneyView;
  projectId: string;
  canApprove: boolean;
  onChanged: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!node) {
    return (
      <aside className="inspector" aria-label="Node inspector">
        <p className="small dim">
          Select a node on the map or in the list to see its owner, dependencies, evidence and next
          action.
        </p>
      </aside>
    );
  }

  const byId = new Map(journey.nodes.map((n) => [n.id, n]));
  const dependsOn = node.dependsOnNodeIds
    .map((id) => byId.get(id))
    .filter(Boolean) as JourneyNodeView[];
  const unlocks = node.unlocksNodeIds
    .map((id) => byId.get(id))
    .filter(Boolean) as JourneyNodeView[];

  async function submit(decision: 'APPROVED' | 'REJECTED' | 'DEFERRED') {
    if (!node) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.approveStation(projectId, node.id, decision, reason, idempotencyKey('station'));
      setMessage(
        `Recorded as ${decision.toLowerCase()}. The decision and its reason are in the audit trail.`,
      );
      setReason('');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'That could not be recorded.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <aside className="inspector" aria-label={`Details for ${node.title}`}>
      <div className="stack stack--tight">
        <span className="label">{node.type.replace(/_/g, ' ').toLowerCase()}</span>
        <h3 className="panel__title">{node.title}</h3>
        {node.subtitle && <p className="small dim">{node.subtitle}</p>}
        <div className="inline inline--tight">
          <StateChip kind="workItem" state={node.status} />
          <Confidence confidence={node.confidence} percentValue={null} />
          {node.onCriticalPath && (
            <span className="chip" data-tone="attention">
              on the critical path
            </span>
          )}
        </div>
        {node.statusCause && <p className="small">{node.statusCause}</p>}
      </div>

      <dl style={{ margin: 0 }}>
        <Field label="Route">{node.route}</Field>
        <Field label="Work item">{node.workItemKey ?? <Unknown />}</Field>
        <Field label="Owner">{node.ownerName ?? 'unassigned'}</Field>
        <Field label="Current driver">{node.driverLabel ?? 'nobody'}</Field>
        <Field label="Agents">
          {node.agents.length === 0
            ? 'none on this segment'
            : node.agents.map((agent) => (
                <span key={agent.id} className="inline inline--tight">
                  {agent.name} <StateChip kind="agent" state={agent.status} />
                </span>
              ))}
        </Field>
        <Field label="Repository">{node.repository ?? <Unknown />}</Field>
        <Field label="Service">{node.service ?? <Unknown />}</Field>
        <Field label="Branch">
          <span className="mono">{node.branch ?? '—'}</span>
        </Field>
        <Field label="Commit">
          <span className="mono">{node.sha ?? '—'}</span>
        </Field>
        <Field label="Pull request">
          <span className="mono">{node.pr ?? '—'}</span>
        </Field>
        <Field label="ETA">
          {node.etaAt ? (
            <span title={absoluteTime(node.etaAt)}>{relativeTime(node.etaAt)}</span>
          ) : (
            <Unknown why="No estimate has been derived for this node yet." />
          )}
        </Field>
        <Field label="Capacity used">
          {node.capacityConsumedPercent === null ? <Unknown /> : `${node.capacityConsumedPercent}%`}
        </Field>
        <Field label="Capacity projected">
          {node.capacityProjectedPercent === null ? (
            <Unknown />
          ) : (
            `${node.capacityProjectedPercent}%`
          )}
        </Field>
        <Field label="Depends on">
          {dependsOn.length === 0 ? 'nothing' : dependsOn.map((n) => n.title).join(', ')}
        </Field>
        <Field label="Unlocks">
          {unlocks.length === 0 ? 'nothing downstream' : unlocks.map((n) => n.title).join(', ')}
        </Field>
      </dl>

      {node.acceptanceCriteria.length > 0 && (
        <div className="stack stack--tight">
          <span className="label">Acceptance criteria</span>
          <ul className="small stack stack--tight" style={{ margin: 0, paddingLeft: 18 }}>
            {node.acceptanceCriteria.map((criterion) => (
              <li key={criterion}>{criterion}</li>
            ))}
          </ul>
        </div>
      )}

      {node.station && (
        <div
          className="card card--toned stack"
          data-tone={node.station.approvalStatus === 'APPROVED' ? 'success' : 'attention'}
        >
          <span className="label">Test station</span>
          <p className="small">{node.station.whatIsTestable}</p>
          {node.station.stagingUrl && (
            <p className="small">
              Staging: <span className="mono">{node.station.stagingUrl}</span>
            </p>
          )}
          {node.station.artifacts.length > 0 && (
            <p className="small dim">
              Artifacts: {node.station.artifacts.map((a) => `${a.label} (${a.ref})`).join(', ')}
            </p>
          )}
          {node.station.knownIssues.length > 0 && (
            <div>
              <span className="label">Known issues</span>
              <ul
                className="small stack stack--tight"
                style={{ margin: '4px 0 0', paddingLeft: 18 }}
              >
                {node.station.knownIssues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </div>
          )}
          <p className="small">
            Approval:{' '}
            <strong>{node.station.approvalStatus.replace(/_/g, ' ').toLowerCase()}</strong>
            {node.station.approvedBy && (
              <span className="dim">
                {' '}
                by {node.station.approvedBy} {relativeTime(node.station.approvedAt)}
              </span>
            )}
          </p>
          {node.station.approvalReason && <p className="tiny dim">{node.station.approvalReason}</p>}

          {canApprove && node.station.approvalStatus !== 'APPROVED' && (
            <div className="stack stack--tight">
              <label className="stack stack--tight">
                <span className="label">Reason (recorded in the audit trail)</span>
                <textarea
                  className="btn"
                  style={{
                    minHeight: 64,
                    padding: 8,
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                  }}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="What you verified, and anything you accepted as a follow-up."
                />
              </label>
              <div className="inline inline--tight">
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  disabled={busy || reason.trim().length < 3}
                  onClick={() => void submit('APPROVED')}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={busy || reason.trim().length < 3}
                  onClick={() => void submit('REJECTED')}
                >
                  Reject
                </button>
                <button
                  type="button"
                  className="btn btn--sm btn--quiet"
                  disabled={busy || reason.trim().length < 3}
                  onClick={() => void submit('DEFERRED')}
                >
                  Defer
                </button>
              </div>
              <p className="tiny dim">
                A green build does not approve a station. Someone has to say they checked it.
              </p>
            </div>
          )}
          {message && (
            <p
              className="small"
              data-tone="success"
              style={{ color: 'var(--success)' }}
              role="status"
            >
              {message}
            </p>
          )}
          {error && (
            <p className="small" data-tone="risk" style={{ color: 'var(--risk)' }} role="alert">
              {error}
            </p>
          )}
        </div>
      )}

      <FreshnessTag freshness={journey.freshness} prefix="projection" />
    </aside>
  );
}
